import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minus, DollarSign, Clock, Ruler, Timer } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useI18n } from "@/i18n/I18nProvider";
import { formatCurrency } from "@/lib/currency";
import { computeSizeMultiplier, multiplierToPercent, normalizePricingRules, percentToMultiplier, type PricingRulesV1 } from "@/lib/pricingRules";
import { mergeBookingPolicy, type BookingPolicyV2 } from "@/lib/bookingPolicy";

const serviceSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  category_id: z.string().min(1, "Category is required"),
  base_price: z.number().min(0.01, "Price must be greater than 0"),
  pricing_type: z.enum(["fixed", "hourly", "per_sqm", "custom"]),
  duration_minutes: z.number().min(15, "Duration must be at least 15 minutes"),
  requires_quote: z.boolean().default(false),
  auto_quote_rules: z.object({
    max_area: z.number().optional(),
    price_multiplier: z.number().optional(),
    urgency_surcharge: z.number().optional(),
  }).optional(),
  included_items: z.array(z.string()).default([]),
  benefit_items: z.array(z.string()).default([]),
  vendor_covers_customer_fee: z.boolean().default(false),
  display_fee_disclaimer: z.boolean().default(false),
  allowed_service_types: z.array(z.string()).default([]),
  pricing_rules: z.any().optional(),
  accepted_payment_methods: z.array(z.string()).default(["card","cash"]),
});

type ServiceForm = z.infer<typeof serviceSchema>;

interface ServiceBuilderProps {
  categories: any[];
  onServiceCreate: (service: ServiceForm) => void;
  existingService?: any;
}

export default function ServiceBuilder({ categories, onServiceCreate, existingService }: ServiceBuilderProps) {
  const { t, tx } = useI18n();
  const [pricingTemplates, setPricingTemplates] = useState([
    { 
      name: t('service.templates.basic_cleaning'), 
      basePrice: 50, 
      type: "per_sqm", 
      multiplier: 2.5,
      description: t('service.templates.basic_cleaning_desc'),
      duration: 60
    },
    { 
      name: t('service.templates.deep_clean'), 
      basePrice: 80, 
      type: "hourly", 
      multiplier: 1.5,
      description: t('service.templates.deep_clean_desc'),
      duration: 120
    },
    { 
      name: t('service.templates.garden_maintenance'), 
      basePrice: 40, 
      type: "fixed", 
      multiplier: 1.0,
      description: t('service.templates.garden_maintenance_desc'),
      duration: 90
    },
    { 
      name: t('service.templates.window_cleaning'), 
      basePrice: 30, 
      type: "per_sqm", 
      multiplier: 1.8,
      description: t('service.templates.window_cleaning_desc'),
      duration: 45
    },
    { 
      name: t('service.templates.carpet_cleaning'), 
      basePrice: 60, 
      type: "per_sqm", 
      multiplier: 3.0,
      description: t('service.templates.carpet_cleaning_desc'),
      duration: 150
    },
    { 
      name: t('service.templates.recurring_package'), 
      basePrice: 100, 
      type: "fixed", 
      multiplier: 0.8,
      description: t('service.templates.recurring_package_desc'),
      duration: 60
    },
  ]);
  
  const { toast } = useToast();

  const form = useForm<ServiceForm>({
    resolver: zodResolver(serviceSchema),
    defaultValues: existingService || {
      name: "",
      description: "",
      category_id: "",
      base_price: 0,
      pricing_type: "fixed",
      duration_minutes: 60,
      requires_quote: false,
      auto_quote_rules: {},
      included_items: [],
      benefit_items: [],
      vendor_covers_customer_fee: false,
      display_fee_disclaimer: false,
      allowed_service_types: [],
      accepted_payment_methods: ["card","cash"],
    },
  });

  const pricingType = form.watch("pricing_type");
  const requiresQuote = form.watch("requires_quote");
  const includedItems = form.watch("included_items");
  const benefitItems = form.watch("benefit_items");
  const vendorCoversFee = form.watch("vendor_covers_customer_fee");
  const displayFeeDisclaimer = form.watch("display_fee_disclaimer");
  const [pricingRules, setPricingRules] = useState<PricingRulesV1>(() => normalizePricingRules(existingService?.pricing_rules || {}));
  const [bookingPolicy, setBookingPolicy] = useState<BookingPolicyV2>(() => mergeBookingPolicy(existingService?.booking_policy));
  const [newCustomTypeLabel, setNewCustomTypeLabel] = useState<string>("");
  const [newCustomTypePct, setNewCustomTypePct] = useState<number>(0);

  const includedSuggestions = [
    t('service.included.dusting'),
    t('service.included.vacuuming'),
    t('service.included.mopping'),
    t('service.included.bathroom_cleaning'),
    t('service.included.kitchen_surfaces'),
  ];
  const benefitSuggestions = [
    t('service.benefits.insured_staff'),
    t('service.benefits.eco_products'),
    t('service.benefits.flexible_scheduling'),
    t('service.benefits.satisfaction_guarantee'),
  ];

  const addIncluded = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (includedItems.includes(trimmed)) return;
    form.setValue("included_items", [...includedItems, trimmed]);
  };
  const removeIncluded = (idx: number) => {
    const next = [...includedItems];
    next.splice(idx, 1);
    form.setValue("included_items", next);
  };
  const addBenefit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (benefitItems.includes(trimmed)) return;
    form.setValue("benefit_items", [...benefitItems, trimmed]);
  };
  const removeBenefit = (idx: number) => {
    const next = [...benefitItems];
    next.splice(idx, 1);
    form.setValue("benefit_items", next);
  };

  const [customInclude, setCustomInclude] = useState("");
  const [customBenefit, setCustomBenefit] = useState("");

  const applyTemplate = (template: any) => {
    form.setValue("base_price", template.basePrice);
    form.setValue("pricing_type", template.type);
    form.setValue("duration_minutes", template.duration);
    form.setValue("description", template.description);
    toast({
      title: t('service.template_applied'),
      description: tx('service.template_applied_desc', { name: template.name }),
    });
  };

  const onSubmit = (data: ServiceForm) => {
    // Guard: pricing bounds must satisfy min <= max (T09).
    const _bmin = (pricingRules as any)?.bounds?.min;
    const _bmax = (pricingRules as any)?.bounds?.max;
    if (typeof _bmin === 'number' && typeof _bmax === 'number' && _bmin > _bmax) {
      toast({
        title: t('service.pricing.bounds_invalid_title'),
        description: t('service.pricing.bounds_invalid'),
        variant: 'destructive',
      });
      return;
    }
    onServiceCreate({ ...data, pricing_rules: pricingRules, booking_policy: bookingPolicy });
    if (!existingService) {
      form.reset();
    }
    toast({
      title: t('service.success'),
      description: existingService ? t('service.updated') : t('service.created'),
    });
  };

  return (
    <Card className="w-full overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 break-words">
          <DollarSign className="h-5 w-5" />
          {t('service.builder.title')}
        </CardTitle>
        <CardDescription className="break-words">
          {t('service.builder.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="max-w-full overflow-x-hidden">
        <Tabs defaultValue="basic" className="w-full min-w-0">
          <TabsList className="flex w-full gap-2 bg-transparent py-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsTrigger className="whitespace-nowrap shrink-0" value="basic">{t('service.tabs.basic_info')}</TabsTrigger>
            <TabsTrigger className="whitespace-nowrap shrink-0" value="pricing">{t('service.tabs.pricing_rules')}</TabsTrigger>
            <TabsTrigger className="whitespace-nowrap shrink-0" value="booking_modes">
              {t('service.tabs.booking_modes')}
            </TabsTrigger>
            <TabsTrigger className="whitespace-nowrap shrink-0" value="templates">{t('service.tabs.templates')}</TabsTrigger>
            <TabsTrigger className="whitespace-nowrap shrink-0" value="content">{t('service.tabs.includes_benefits')}</TabsTrigger>
          </TabsList>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('service.fields.name')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('service.placeholders.name')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('service.fields.category')}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('service.placeholders.category')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {categories.map((category) => (
                              <SelectItem key={category.id} value={category.id}>
                                {category.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('service.fields.description')}</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder={t('service.placeholders.description')}
                          rows={4}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Allowed service types (optional) */}
                <div className="space-y-2">
                  <FormLabel>{t('onboard.services.types_title')}</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { key: 'standard', label: 'Standard' },
                      { key: 'deep_clean', label: 'Deep Clean' },
                      { key: 'eco_friendly', label: 'Eco-Friendly' },
                      { key: 'premium', label: 'Premium' },
                    ].map(opt => {
                      const selected = (form.watch('allowed_service_types') || []).includes(opt.key);
                      return (
                        <label key={opt.key} className="flex items-center gap-2 p-2 border rounded">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const current = form.getValues('allowed_service_types') || [];
                              form.setValue(
                                'allowed_service_types',
                                e.target.checked ? [...current, opt.key] : current.filter((k: string) => k !== opt.key)
                              );
                            }}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Accepted payment methods */}
                <div className="space-y-2">
                  <FormLabel>{t('onboard.services.payment_methods')}</FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[
                      { key: 'card', label: 'Carta' },
                      { key: 'cash', label: 'Contanti' },
                      { key: 'invoice', label: 'Solo Fattura (bonifico dopo)' },
                    ].map(opt => {
                      const selected = (form.watch('accepted_payment_methods') || []).includes(opt.key);
                      return (
                        <label key={opt.key} className="flex items-center gap-2 p-2 border rounded">
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const current = form.getValues('accepted_payment_methods') || [];
                              form.setValue(
                                'accepted_payment_methods',
                                e.target.checked ? [...current, opt.key] : current.filter((k: string) => k !== opt.key)
                              );
                            }}
                          />
                          <span className="text-sm">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="duration_minutes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {t('service.fields.duration')}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="number"
                          placeholder="60"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </TabsContent>

              <TabsContent value="pricing" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="pricing_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pricing Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed Price</SelectItem>
                            <SelectItem value="hourly">Per Hour</SelectItem>
                            <SelectItem value="per_sqm">Per Square Meter</SelectItem>
                            <SelectItem value="custom">Custom Estimate</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="base_price"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Base Price ($)
                          {pricingType === "per_sqm" && <Ruler className="h-4 w-4" />}
                        </FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.01"
                            placeholder="50.00"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center space-x-2 p-4 border rounded-lg">
                  <FormField
                    control={form.control}
                    name="requires_quote"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-y-0 space-x-3">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1">
                          <FormLabel>Requires Custom Quote</FormLabel>
                          <p className="text-sm text-muted-foreground">
                            Enable this for complex services that need personalized pricing
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>

                {requiresQuote && (
                  <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                    <h4 className="font-medium">Auto-Quote Rules</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Max Area (sqm)</Label>
                        <Input 
                          type="number"
                          placeholder="100"
                          onChange={(e) => {
                            const rules = form.getValues("auto_quote_rules") || {};
                            form.setValue("auto_quote_rules", {
                              ...rules,
                              max_area: parseFloat(e.target.value) || undefined
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Price Multiplier</Label>
                        <Input 
                          type="number"
                          step="0.1"
                          placeholder="1.5"
                          onChange={(e) => {
                            const rules = form.getValues("auto_quote_rules") || {};
                            form.setValue("auto_quote_rules", {
                              ...rules,
                              price_multiplier: parseFloat(e.target.value) || undefined
                            });
                          }}
                        />
                      </div>
                      <div>
                        <Label>Urgency Surcharge (%)</Label>
                        <Input 
                          type="number"
                          placeholder="20"
                          onChange={(e) => {
                            const rules = form.getValues("auto_quote_rules") || {};
                            form.setValue("auto_quote_rules", {
                              ...rules,
                              urgency_surcharge: parseFloat(e.target.value) || undefined
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Fee coverage & disclaimer toggles */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <div className="flex items-start gap-3">
                    <Switch
                      checked={vendorCoversFee}
                      onCheckedChange={(v) => form.setValue('vendor_covers_customer_fee', v)}
                    />
                    <div>
                      <div className="font-medium">Vendor covers customer fee</div>
                      <div className="text-sm text-muted-foreground">If enabled, the customer payment/platform fee is absorbed by the vendor and is not added separately to the customer total.</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Switch
                      checked={displayFeeDisclaimer}
                      onCheckedChange={(v) => form.setValue('display_fee_disclaimer', v)}
                    />
                    <div>
                      <div className="font-medium">Show fee disclaimer to customers</div>
                      <div className="text-sm text-muted-foreground">Displays a short disclaimer in the price summary.</div>
                    </div>
                  </div>
                </div>

                {/* Pricing Modifiers (centralized per service/company) */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h4 className="font-medium">{t('service.pricing.modifiers')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t(
                          'service.pricing.modifiers_help',
                          "Permetti che il prezzo finale di questo servizio cambi in base alle scelte del cliente in fase di booking (metratura, tipologia). Se non attivi questa opzione, il prezzo resta fisso e uguale per tutti."
                        )}
                      </p>
                    </div>
                    {pricingType !== "per_sqm" && pricingType !== "hourly" && (
                      <Switch
                        checked={(pricingRules as any)?.enabled !== false}
                        onCheckedChange={(v) =>
                          setPricingRules((prev) => ({ ...prev, enabled: v }))
                        }
                      />
                    )}
                  </div>
                  {(pricingType === "per_sqm" || pricingType === "hourly") && (
                    <div className="text-xs text-muted-foreground p-2 bg-muted/40 border rounded">
                      {t(
                        'service.pricing.forced_on',
                        "Per servizi a metro quadro o a ore le regole di prezzo sono sempre attive: il totale dipende dalla dimensione/durata indicata dal cliente."
                      )}
                    </div>
                  )}

                  {(pricingRules as any)?.enabled === false && pricingType !== "per_sqm" && pricingType !== "hourly" && (
                    <div className="text-sm text-muted-foreground p-3 border rounded-lg bg-muted/30">
                      {t(
                        'service.pricing.disabled_explain',
                        "Le regole di prezzo sono disattivate per questo servizio. Il cliente vedra direttamente il prezzo base e non gli verra chiesto di specificare metratura o tipologia in fase di prenotazione."
                      )}
                    </div>
                  )}

                  {((pricingRules as any)?.enabled !== false || pricingType === "per_sqm" || pricingType === "hourly") && (
                  <div className="space-y-2">
                    <div className="font-medium">{t('service.pricing.bounds')}</div>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        'service.pricing.bounds_help',
                        "Imposta i limiti entro cui il prezzo finale puo variare dopo l'applicazione delle regole. Il cliente vede questo range nel riepilogo come 'prezzo concordato con il fornitore'. Lascia vuoto un campo per non applicare quel limite."
                      )}
                    </p>
                    {(() => {
                      const _bmin = (pricingRules as any)?.bounds?.min;
                      const _bmax = (pricingRules as any)?.bounds?.max;
                      const _hasBoth = typeof _bmin === 'number' && typeof _bmax === 'number';
                      const boundsInvalid = _hasBoth && _bmin > _bmax;
                      return (
                        <>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label>{t('service.pricing.bounds_min')}</Label>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                aria-invalid={boundsInvalid}
                                className={boundsInvalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
                                value={(pricingRules as any)?.bounds?.min ?? ''}
                                placeholder={t('service.pricing.bounds_min_placeholder')}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const n = raw === '' ? undefined : (parseFloat(raw) || 0);
                                  setPricingRules((prev) => ({
                                    ...prev,
                                    bounds: { ...((prev as any).bounds || {}), min: n },
                                  }));
                                }}
                              />
                            </div>
                            <div>
                              <Label>{t('service.pricing.bounds_max')}</Label>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                aria-invalid={boundsInvalid}
                                className={boundsInvalid ? 'border-destructive focus-visible:ring-destructive' : undefined}
                                value={(pricingRules as any)?.bounds?.max ?? ''}
                                placeholder={t('service.pricing.bounds_max_placeholder')}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  const n = raw === '' ? undefined : (parseFloat(raw) || 0);
                                  setPricingRules((prev) => ({
                                    ...prev,
                                    bounds: { ...((prev as any).bounds || {}), max: n },
                                  }));
                                }}
                              />
                            </div>
                          </div>
                          {boundsInvalid && (
                            <p className="text-xs text-destructive mt-1" role="alert">
                              {t('service.pricing.bounds_invalid')}
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  )}

                  {((pricingRules as any)?.enabled !== false || pricingType === "per_sqm" || pricingType === "hourly") && (
                  <>
                  {/* SQM rules */}
                  <div className="space-y-2">
                    <div className="font-medium flex items-center gap-2"><Ruler className="h-4 w-4" /> {t('service.pricing.sqm')}</div>
                    {/* Modalità Lineare (legacy) rimossa: ora si lavora sempre a scaglioni.
                        Forziamo sqm.mode='tiers' qualora un servizio salvato in precedenza
                        avesse 'linear'. */}
                    {String((pricingRules as any)?.sqm?.mode || 'tiers') !== 'tiers' && (() => {
                      // one-shot migration on mount-render
                      setTimeout(() => setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers' } })), 0);
                      return null;
                    })()}

                    <p className="text-xs text-muted-foreground">
                      {t('service.pricing.sqm_unit_hint')}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <Label>{t('service.pricing.sqm_min')}</Label>
                        <Input type="number" value={pricingRules.sqm?.min ?? 50} onChange={(e) => setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), min: parseInt(e.target.value || '0', 10) || 0 } }))} />
                      </div>
                      <div>
                        <Label>{t('service.pricing.sqm_max')}</Label>
                        <Input type="number" value={pricingRules.sqm?.max ?? 500} onChange={(e) => setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), max: parseInt(e.target.value || '0', 10) || 0 } }))} />
                      </div>
                      <div>
                        <Label>{t('service.pricing.sqm_step')}</Label>
                        <Input type="number" value={pricingRules.sqm?.step ?? 10} onChange={(e) => setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), step: parseInt(e.target.value || '0', 10) || 1 } }))} />
                      </div>
                      {/* Baseline + Scale rimossi: erano parametri della modalità lineare legacy */}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('service.pricing.sqm_tiers_help')}
                    </div>

                    {String((pricingRules as any)?.sqm?.mode || 'tiers') === 'tiers' && (
                      <div className="mt-3 space-y-2">
                        {(() => {
                          try {
                            const rr = normalizePricingRules(pricingRules);
                            const tiers = rr.sqm.tiers || [];
                            if (tiers.length === 0) return null;
                            const min = rr.sqm.min;
                            const max = rr.sqm.max;
                            const coveredMin = Math.min(...tiers.map((t: any) => Number(t.from)));
                            const coveredMax = Math.max(...tiers.map((t: any) => Number(t.to)));
                            if (coveredMin > min || coveredMax < max) {
                              return (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                  Attenzione: i tuoi scaglioni coprono {coveredMin}–{coveredMax} m², ma lo slider cliente va da {min} a {max} m².
                                  Aggiungi scaglioni fino a coprire tutto l’intervallo.
                                </div>
                              );
                            }
                          } catch {}
                          return null;
                        })()}
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">{t('service.pricing.tiers')}</div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const rr = normalizePricingRules(pricingRules);
                              const tiers = Array.isArray(rr.sqm.tiers) ? [...rr.sqm.tiers] : [];
                              const min = rr.sqm.min;
                              const max = rr.sqm.max;
                              const last = tiers.length > 0 ? tiers[tiers.length - 1] : null;
                              const from = last ? Math.min(max, Number(last.to) + 1) : min;
                              const to = Math.min(max, from + Math.max(1, rr.sqm.step));
                              tiers.push({ from, to, multiplier: 1 });
                              setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers', tiers } }));
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" /> {t('service.pricing.add_tier')}
                          </Button>
                        </div>
                        {(() => {
                          const rawTiers = Array.isArray((pricingRules as any)?.sqm?.tiers)
                            ? (pricingRules as any).sqm.tiers
                            : [];
                          if (rawTiers.length === 0) {
                            return (
                              <div className="text-sm text-muted-foreground">
                                {t('service.pricing.no_tiers')}
                              </div>
                            );
                          }
                          return (
                            <div className="space-y-2">
                              {rawTiers.map((tier: any, idx: number) => (
                                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 border rounded">
                                  <div className="md:col-span-3">
                                    <Label>Da (m²)</Label>
                                    <Input
                                      type="number"
                                      value={tier.from}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value || '0', 10) || 0;
                                        const next = [...rawTiers];
                                        next[idx] = { ...next[idx], from: v };
                                        setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers', tiers: next } }));
                                      }}
                                    />
                                  </div>
                                  <div className="md:col-span-3">
                                    <Label>A (m²)</Label>
                                    <Input
                                      type="number"
                                      value={tier.to}
                                      onChange={(e) => {
                                        const v = parseInt(e.target.value || '0', 10) || 0;
                                        const next = [...rawTiers];
                                        next[idx] = { ...next[idx], to: v };
                                        setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers', tiers: next } }));
                                      }}
                                    />
                                  </div>
                                  <div className="md:col-span-4">
                                    <Label>% aumento</Label>
                                    <Input
                                      type="number"
                                      value={multiplierToPercent(tier.multiplier)}
                                      onChange={(e) => {
                                        const pct = parseFloat(e.target.value || '0') || 0;
                                        const next = [...rawTiers];
                                        next[idx] = { ...next[idx], multiplier: percentToMultiplier(pct) };
                                        setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers', tiers: next } }));
                                      }}
                                    />
                                  </div>
                                  <div className="md:col-span-2 flex justify-end">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const next = [...rawTiers];
                                        next.splice(idx, 1);
                                        setPricingRules(prev => ({ ...prev, sqm: { ...(prev.sqm || {}), mode: 'tiers', tiers: next } }));
                                      }}
                                    >
                                      <Minus className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Urgency multipliers used to live here. Removed: the
                      urgent surcharge is now configured exclusively in the
                      Lead time tab (booking_policy.urgent.pricing_value) and
                      applied automatically by the server based on the date
                      the customer chooses. */}

                  {/* Service types & custom options */}
                  <div className="space-y-2">
                    <div className="font-medium">{t('service.pricing.types')}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { key: 'standard', label: 'Standard' },
                        { key: 'deep_clean', label: 'Deep Clean' },
                        { key: 'eco_friendly', label: 'Eco-Friendly' },
                        { key: 'premium', label: 'Premium' },
                      ].map(opt => (
                        <div key={opt.key} className="flex items-center justify-between gap-3 p-3 border rounded">
                          <div className="font-medium">{opt.label}</div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">%</span>
                            <Input
                              className="w-24"
                              type="number"
                              value={multiplierToPercent((pricingRules.types as any)?.[opt.key] ?? 1)}
                              onChange={(e) => setPricingRules(prev => ({ ...prev, types: { ...(prev.types || {}), [opt.key]: percentToMultiplier(parseFloat(e.target.value || '0') || 0) } }))}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-2">
                      <div className="text-sm font-medium mb-2">{t('service.pricing.custom')}</div>
                      <div className="flex flex-col md:flex-row gap-2">
                        <Input value={newCustomTypeLabel} onChange={(e) => setNewCustomTypeLabel(e.target.value)} placeholder="Nome opzione (es. 'Post-ristrutturazione')" />
                        <Input className="md:w-40" type="number" value={newCustomTypePct} onChange={(e) => setNewCustomTypePct(parseFloat(e.target.value || '0') || 0)} placeholder="% aumento" />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            const label = (newCustomTypeLabel || "").trim();
                            if (!label) return;
                            const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || `custom_${Date.now()}`;
                            const next = [...(pricingRules.types?.custom || [])].filter((x) => x.key !== key);
                            next.push({ key, label, multiplier: percentToMultiplier(newCustomTypePct) });
                            setPricingRules(prev => ({ ...prev, types: { ...(prev.types || {}), custom: next } }));
                            setNewCustomTypeLabel("");
                            setNewCustomTypePct(0);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" /> {t('service.pricing.add')}
                        </Button>
                      </div>
                      {(pricingRules.types?.custom || []).length > 0 && (
                        <div className="mt-3 space-y-2">
                          {(pricingRules.types?.custom || []).map((c) => (
                            <div key={c.key} className="flex items-center justify-between gap-2 p-2 border rounded">
                              <div className="text-sm font-medium">{c.label}</div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">%</span>
                                <Input
                                  className="w-24"
                                  type="number"
                                  value={multiplierToPercent(c.multiplier)}
                                  onChange={(e) => {
                                    const pct = parseFloat(e.target.value || '0') || 0;
                                    const updated = (pricingRules.types?.custom || []).map((x) => x.key === c.key ? { ...x, multiplier: percentToMultiplier(pct) } : x);
                                    setPricingRules(prev => ({ ...prev, types: { ...(prev.types || {}), custom: updated } }));
                                  }}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const filtered = (pricingRules.types?.custom || []).filter((x) => x.key !== c.key);
                                    setPricingRules(prev => ({ ...prev, types: { ...(prev.types || {}), custom: filtered } }));
                                  }}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  </>
                  )}

                  {/* Quick sanity preview */}
                  <div className="mt-2 text-xs text-muted-foreground">
                    {t('service.pricing.preview')}{" "}
                    {(() => {
                      try {
                        const rr = normalizePricingRules(pricingRules);
                        const base = Number(form.getValues('base_price') || 0);
                        const sizeMult = computeSizeMultiplier(130, rr.sqm);
                        const urgMult = rr.urgency.urgent;
                        const total = Math.round(base * sizeMult * urgMult);
                        return `${formatCurrency(total)} (x${(sizeMult * urgMult).toFixed(2)})`;
                      } catch { return ''; }
                    })()}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="booking_modes" className="space-y-4">
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center gap-2 font-medium">
                    <Timer className="h-4 w-4" />
                    {t('service.booking_policy.title')}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'service.booking_policy.help',
                      'Il sistema calcola automaticamente instant / richiesta / urgente in base a data e ora del servizio (fuso Europe/Rome). Qui abiliti le fasce e gli SLA.'
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2">
                    <div className="font-medium">{t('service.booking_policy.instant')}</div>
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">{t('service.booking_policy.enabled')}</Label>
                      <Switch
                        checked={bookingPolicy.instant?.enabled !== false}
                        onCheckedChange={(v) =>
                          setBookingPolicy((p) => ({ ...p, instant: { ...p.instant, enabled: v } }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('service.booking_policy.min_notice_h')}</Label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={bookingPolicy.instant?.min_notice_hours ?? 48}
                        onChange={(e) =>
                          setBookingPolicy((p) => ({
                            ...p,
                            instant: { ...p.instant, min_notice_hours: parseFloat(e.target.value) || 48 },
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="font-medium">{t('service.booking_policy.request')}</div>
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">{t('service.booking_policy.enabled')}</Label>
                      <Switch
                        checked={bookingPolicy.request?.enabled !== false}
                        onCheckedChange={(v) =>
                          setBookingPolicy((p) => ({ ...p, request: { ...p.request, enabled: v } }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('service.booking_policy.min_notice_h')}</Label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={bookingPolicy.request?.min_notice_hours ?? 24}
                        onChange={(e) =>
                          setBookingPolicy((p) => ({
                            ...p,
                            request: { ...p.request, min_notice_hours: parseFloat(e.target.value) || 24 },
                          }))
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('service.booking_policy.sla_min')}</Label>
                      <Input
                        type="number"
                        min={1}
                        step={1}
                        value={bookingPolicy.request?.vendor_response_sla_minutes ?? 30}
                        onChange={(e) =>
                          setBookingPolicy((p) => ({
                            ...p,
                            request: {
                              ...p.request,
                              vendor_response_sla_minutes: parseInt(e.target.value || '30', 10) || 30,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                  <div className="space-y-2 md:col-span-2">
                    <div className="font-medium">{t('service.booking_policy.urgent')}</div>
                    <div className="text-sm text-muted-foreground p-3 bg-muted/40 border rounded">
                      {t(
                        'service.booking_policy.urgent_explain',
                        "Possono arrivarti prenotazioni con poco preavviso: il sistema riconosce automaticamente quando un cliente sta prenotando entro la fascia urgente che imposti qui (es. 12-24 ore). Per compensare lo sforzo extra e i tempi stretti di preparazione, puoi applicare una maggiorazione sul prezzo base. Il cliente vede la maggiorazione esplicitata nel riepilogo del prezzo, e tu la ricevi sul totale del booking. Non c'e bisogno di chiedere al cliente quanto e urgente: lo deduciamo dalla data che sceglie."
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-sm">{t('service.booking_policy.enabled')}</Label>
                      <Switch
                        checked={bookingPolicy.urgent?.enabled !== false}
                        onCheckedChange={(v) =>
                          setBookingPolicy((p) => ({ ...p, urgent: { ...p.urgent, enabled: v } }))
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs">{t('service.booking_policy.min_notice_h')}</Label>
                        <Input
                          type="number"
                          min={1}
                          value={bookingPolicy.urgent?.min_notice_hours ?? 12}
                          onChange={(e) =>
                            setBookingPolicy((p) => ({
                              ...p,
                              urgent: { ...p.urgent, min_notice_hours: parseFloat(e.target.value) || 12 },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('service.booking_policy.max_notice_h')}</Label>
                        <Input
                          type="number"
                          min={1}
                          value={bookingPolicy.urgent?.max_notice_hours ?? 24}
                          onChange={(e) =>
                            setBookingPolicy((p) => ({
                              ...p,
                              urgent: { ...p.urgent, max_notice_hours: parseFloat(e.target.value) || 24 },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('service.booking_policy.sla_min')}</Label>
                        <Input
                          type="number"
                          min={1}
                          value={bookingPolicy.urgent?.vendor_response_sla_minutes ?? 60}
                          onChange={(e) =>
                            setBookingPolicy((p) => ({
                              ...p,
                              urgent: {
                                ...p.urgent,
                                vendor_response_sla_minutes: parseInt(e.target.value || '60', 10) || 60,
                              },
                            }))
                          }
                        />
                      </div>
                      <div>
                        <Label className="text-xs">{t('service.booking_policy.urgent_mult')}</Label>
                        <Input
                          type="number"
                          min={1}
                          step={0.05}
                          value={bookingPolicy.urgent?.pricing_value ?? 1.5}
                          onChange={(e) =>
                            setBookingPolicy((p) => ({
                              ...p,
                              urgent: {
                                ...p.urgent,
                                pricing_strategy: 'multiplier',
                                pricing_value: parseFloat(e.target.value) || 1.5,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-muted-foreground p-3 border rounded-lg bg-muted/40">
                  {t(
                    'service.booking_policy.cutoff_note',
                    'Stesso giorno: prenotazioni consentite solo prima delle 08:00 (Europe/Rome), come da policy server.'
                  )}
                </div>
              </TabsContent>

              {/* Includes & Benefits */}
              <TabsContent value="content" className="space-y-6">
                <div className="space-y-2">
                  <h4 className="font-medium">What's Included</h4>
                  <div className="flex flex-wrap gap-2">
                    {includedSuggestions.map((s) => (
                      <Button key={s} type="button" variant="outline" size="sm" className="border-gray-300"
                        onClick={() => addIncluded(s)}>
                        + {s}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Input value={customInclude} onChange={(e) => setCustomInclude(e.target.value)} placeholder="Other (type here)" />
                    <Button type="button" variant="outline" className="border-gray-300" onClick={() => { addIncluded(customInclude); setCustomInclude(""); }}>Add</Button>
                  </div>
                  {includedItems.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {includedItems.map((txt, idx) => (
                        <span key={idx} className="px-2 py-1 rounded-full text-sm bg-gray-100 border border-gray-200 flex items-center gap-2">
                          {txt}
                          <button type="button" onClick={() => removeIncluded(idx)} className="text-gray-500 hover:text-gray-700">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Why Choose Us (Benefits)</h4>
                  <div className="flex flex-wrap gap-2">
                    {benefitSuggestions.map((s) => (
                      <Button key={s} type="button" variant="outline" size="sm" className="border-gray-300"
                        onClick={() => addBenefit(s)}>
                        + {s}
                      </Button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Input value={customBenefit} onChange={(e) => setCustomBenefit(e.target.value)} placeholder="Other (type here)" />
                    <Button type="button" variant="outline" className="border-gray-300" onClick={() => { addBenefit(customBenefit); setCustomBenefit(""); }}>Add</Button>
                  </div>
                  {benefitItems.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {benefitItems.map((txt, idx) => (
                        <span key={idx} className="px-2 py-1 rounded-full text-sm bg-gray-100 border border-gray-200 flex items-center gap-2">
                          {txt}
                          <button type="button" onClick={() => removeBenefit(idx)} className="text-gray-500 hover:text-gray-700">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="templates" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {pricingTemplates.map((template, index) => (
                    <Card key={index} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">{template.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{formatCurrency(template.basePrice)}</Badge>
                          <Badge variant="outline">{template.type}</Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          type="button"
                          variant="outline" 
                          size="sm"
                          onClick={() => applyTemplate(template)}
                          className="w-full"
                        >
                          Apply Template
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950">
                  <h4 className="font-medium mb-2">Template Benefits</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Quick setup with industry-standard pricing</li>
                    <li>• Pre-configured multipliers for different service types</li>
                    <li>• Easy to customize after applying</li>
                  </ul>
                </div>
              </TabsContent>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <Button type="submit">
                  {existingService ? "Update Service" : "Create Service"}
                </Button>
              </div>
            </form>
          </Form>
        </Tabs>
      </CardContent>
    </Card>
  );
}